import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSearchExecution } from '../../context/SearchExecutionContext';
import { useConnectionStatus } from '../../hooks/useWebSocket';
import { fetchWithRetry } from '../../hooks/useApiWithRetry';
import { cacheManager } from '../../utils/cacheManager';
import { queryTemplatesAPI } from '../../services/api';
import Tooltip from '../UI/Tooltip';

const QueryTemplateManager = React.memo(() => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTemplate, setRenameTemplate] = useState(null);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const pollIntervalRef = useRef(null);
  
  // Use contexts
  const { statusColor, statusText, showReconnectButton, reconnect } = useConnectionStatus();
  const { executionState, startExecution, updateExecutionStatus, completeExecution, isExecuting } = useSearchExecution();

  const loadData = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to load from cache first for immediate display
      const cachedTemplates = cacheManager.get('templates');
      const cachedCategories = cacheManager.get('categories');
      
      if (cachedTemplates && cachedCategories) {
        setTemplates(cachedTemplates);
        setCategories(cachedCategories);
        setLoading(false);
        // Continue to fetch fresh data in background
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      // Load templates and categories in parallel with retry
      const [templatesResponse, categoriesResponse] = await Promise.all([
        fetchWithRetry(`${apiUrl}/query-templates`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }),
        fetchWithRetry(`${apiUrl}/query-templates/categories`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        })
      ]);

      // Handle 401 errors - don't retry, the interceptor will handle token refresh
      if (templatesResponse.status === 401 || categoriesResponse.status === 401) {
        console.log('Authentication failed in QueryTemplateManager');
        setError(null); // Don't show error, auth will redirect
        return;
      }

      if (templatesResponse.ok && categoriesResponse.ok) {
        const templatesData = await templatesResponse.json();
        const categoriesData = await categoriesResponse.json();
        
        const templates = templatesData.templates || [];
        const categories = categoriesData.categories || [];
        
        // Update state
        setTemplates(templates);
        setCategories(categories);
        setError(null); // Clear any previous errors
        
        // Cache the data
        cacheManager.set('templates', templates);
        cacheManager.set('categories', categories);
      } else {
        throw new Error('Failed to load data');
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      
      // If we have cached data and network failed, keep using cached data
      const cachedTemplates = cacheManager.get('templates');
      const cachedCategories = cacheManager.get('categories');
      
      if (cachedTemplates && cachedCategories) {
        setTemplates(cachedTemplates);
        setCategories(cachedCategories);
        setError(null); // Don't show error if we have cached data
      } else if (retryCount < 1 && !error.message?.includes('401')) {
        // Only retry for non-auth errors
        setTimeout(() => {
          loadData(retryCount + 1);
        }, 2000);
      } else if (!error.message?.includes('401')) {
        // Only show error for non-auth failures
        setError('Failed to load query templates. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only proceed if we have a valid user and token
    if (user && localStorage.getItem('accessToken')) {
      loadData();
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [user, loadData]);

  // Resume polling if there's an active search
  useEffect(() => {
    if (executionState.isRunning && executionState.batchId && !pollIntervalRef.current) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      // Resume polling for the existing batch
      pollIntervalRef.current = setInterval(async () => {
        try {
          const response = await fetchWithRetry(`${apiUrl}/search-execution/batches/${executionState.batchId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            }
          });
          
          if (response.ok) {
            const batchData = await response.json();
            console.log('📊 Resuming batch status polling:', batchData.batch.status);
            
            // Check if batch is completed
            if (batchData.batch.status === 'completed') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              completeExecution('completed');
              navigate(`/results/${executionState.batchId}`);
            } else if (batchData.batch.status === 'failed') {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              completeExecution('failed');
              alert('Execution failed');
            }
          }
        } catch (error) {
          console.error('Error polling batch status:', error);
        }
      }, 3000); // Poll every 3 seconds
    }
    
    return () => {
      // Cleanup on unmount but don't clear the execution state
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [executionState.isRunning, executionState.batchId, navigate, completeExecution]);


  const handleTemplateSelection = (templateId) => {
    const newSelected = new Set(selectedTemplates);
    if (newSelected.has(templateId)) {
      newSelected.delete(templateId);
    } else {
      newSelected.add(templateId);
    }
    setSelectedTemplates(newSelected);
  };

  const getFilteredTemplates = useCallback(() => {
    if (selectedCategory === 'all') {
      return templates;
    }
    return templates.filter(t => t.categoryId === selectedCategory);
  }, [selectedCategory, templates]);

  const handleSelectAll = useCallback(() => {
    const filteredTemplates = getFilteredTemplates();
    if (selectedTemplates.size === filteredTemplates.length) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(filteredTemplates.map(t => t.id)));
    }
  }, [getFilteredTemplates, selectedTemplates.size]);

  const executeSelectedTemplates = async () => {
    if (selectedTemplates.size === 0) {
      alert('Please select at least one template to execute');
      return;
    }

    // Check if a search is already running
    if (isExecuting()) {
      alert('A search is already in progress. Please wait for it to complete.');
      return;
    }

    try {
      updateExecutionStatus('starting');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      const response = await fetchWithRetry(`${apiUrl}/search-execution/batches?execute=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          name: `Batch Execution - ${new Date().toLocaleString()}`,
          template_ids: Array.from(selectedTemplates)
        })
      });

      if (response.ok) {
        const data = await response.json();
        const batchName = `Batch Execution - ${new Date().toLocaleString()}`;
        startExecution(data.batch.id, batchName);
        
        // Simple polling for status updates
        pollIntervalRef.current = setInterval(async () => {
          try {
            const response = await fetchWithRetry(`${apiUrl}/search-execution/batches/${data.batch.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
              }
            });
            
            if (response.ok) {
              const batchData = await response.json();
              console.log('📊 Polling batch status:', batchData.batch.status);
              
              // Check if batch is completed
              if (batchData.batch.status === 'completed') {
                clearInterval(pollIntervalRef.current);
                completeExecution('completed');
                navigate(`/results/${data.batch.id}`);
              } else if (batchData.batch.status === 'failed') {
                clearInterval(pollIntervalRef.current);
                completeExecution('failed');
                alert('Execution failed');
              }
            }
          } catch (error) {
            console.error('Error polling batch status:', error);
          }
        }, 3000); // Poll every 3 seconds to avoid rate limiting
      } else {
        throw new Error('Failed to start execution');
      }
    } catch (error) {
      console.error('Execution failed:', error);
      completeExecution('error');
      alert('Failed to start execution');
    }
  };

  const downloadSampleCSV = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/query-templates/sample-csv`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sample-query-templates.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Download failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to download sample CSV:', error);
    }
  };

  const deleteTemplate = async (templateId, templateName) => {
    // Prevent deletion during execution
    if (isExecuting()) {
      alert('Cannot delete templates while execution is in progress');
      return;
    }

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the template "${templateName}"?\n\nThis action cannot be undone.`
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetchWithRetry(`${apiUrl}/query-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.ok) {
        // Remove from selected templates if it was selected
        const newSelected = new Set(selectedTemplates);
        newSelected.delete(templateId);
        setSelectedTemplates(newSelected);
        
        // Clear cache and reload
        cacheManager.remove('templates');
        cacheManager.remove('categories');
        await loadData();
        
        alert('Template deleted successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert(`Failed to delete template: ${error.message}`);
    }
  };

  const deleteSelectedTemplates = async () => {
    // Prevent deletion during execution
    if (isExecuting()) {
      alert('Cannot delete templates while execution is in progress');
      return;
    }

    if (selectedTemplates.size === 0) {
      alert('Please select templates to delete');
      return;
    }

    const isConfirmed = window.confirm(
      `Are you sure you want to delete ${selectedTemplates.size} selected template${selectedTemplates.size > 1 ? 's' : ''}?\n\nThis action cannot be undone.`
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const templateIds = Array.from(selectedTemplates);
      
      // Delete each selected template
      const deletePromises = templateIds.map(templateId => 
        fetchWithRetry(`${apiUrl}/query-templates/${templateId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        })
      );
      
      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r.ok).length;
      
      if (successCount > 0) {
        setSelectedTemplates(new Set());
        // Clear cache and reload
        cacheManager.remove('templates');
        cacheManager.remove('categories');
        await loadData();
        
        if (successCount === templateIds.length) {
          alert(`Successfully deleted ${successCount} template${successCount > 1 ? 's' : ''}`);
        } else {
          alert(`Deleted ${successCount} of ${templateIds.length} templates. Some deletions failed.`);
        }
      } else {
        throw new Error('Failed to delete any templates');
      }
    } catch (error) {
      console.error('Failed to delete selected templates:', error);
      alert(`Failed to delete selected templates: ${error.message}`);
    }
  };

  const filteredTemplates = React.useMemo(() => getFilteredTemplates(), [getFilteredTemplates]);

  if (!user) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sign In Required</h3>
          <p className="text-gray-500 mb-6">Please sign in to access query template management and automated search execution.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Error Loading Templates</h3>
        <p className={`${isDark ? 'text-gray-300' : 'text-gray-500'} mb-4`}>{error}</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => loadData()}
            className="btn-glass-accent px-4 py-2 rounded-lg text-mono font-medium text-sm"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="btn-glass px-4 py-2 rounded-lg text-mono font-medium text-sm"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      {/* Global Search Status Notification */}
      {executionState.isRunning && (
        <div className="glass-card px-6 py-4">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-5 w-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div>
              <p className="text-mono font-semibold text-yellow-500">Search in Progress</p>
              <p className="text-sm text-gray-400 mt-1">
                You will be notified by email when the results are ready.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header and Usage in a grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-card rounded-2xl p-6 dashboard-optimized">
          <h1 className={`heading-mono text-xl text-glow mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            New Analysis
          </h1>
          <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Create competitive search queries that will be tested across AI search engines (Claude, ChatGPT, Gemini, Perplexity) to find your competitors.
          </p>
        </div>
        
      </div>

      {/* Connection Status */}
      {showReconnectButton && (
        <div className={`glass-card rounded-lg p-4 flex items-center justify-between ${
          isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-300'
        } border`}>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${statusColor} animate-pulse`}></div>
            <span className={`text-mono text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>
              {statusText} - Real-time updates unavailable
            </span>
          </div>
          <button
            onClick={reconnect}
            className="btn-glass text-xs px-3 py-1"
          >
            Reconnect
          </button>
        </div>
      )}

      {/* Quick Actions */}
      <div className="glass-card rounded-2xl p-6 dashboard-optimized">
        <h2 className={`heading-mono text-lg mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Tooltip content="Import multiple search queries from a CSV file. Each query will be tested across all AI search engines." position="bottom">
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-glass-accent px-4 py-2 rounded-lg text-mono font-medium text-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Import CSV Templates
            </button>
          </Tooltip>
          
          <Tooltip content="Download a CSV template with example search queries to see the correct format" position="bottom">
            <button
              onClick={downloadSampleCSV}
              className="btn-glass px-4 py-2 rounded-lg text-mono font-medium text-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Sample CSV
            </button>
          </Tooltip>

          {selectedTemplates.size > 0 && (
            <div className="flex flex-col gap-2">
              <Tooltip content="Run selected queries across all AI search engines (Claude, ChatGPT, Gemini, Perplexity) to find and analyze your competitors" position="top">
                <button
                  onClick={executeSelectedTemplates}
                  disabled={isExecuting() || selectedTemplates.size === 0}
                  className="glass-badge-success px-6 py-3 rounded-xl text-mono font-semibold flex items-center gap-3 disabled:opacity-50 dashboard-optimized"
              >
                {executionState.status === 'starting' && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {executionState.isRunning && (
                  <svg className="animate-pulse w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
                {!executionState.isRunning && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-9-10h10a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" />
                  </svg>
                )}
                {isExecuting() ? 'Search in Progress...' : `Execute ${selectedTemplates.size} Templates`}
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      {templates.length === 0 ? (
        // Empty State
        <div className="glass-card rounded-2xl p-12 text-center dashboard-optimized">
          <div className="glass-dark rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className={`heading-mono text-base mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>Start Your Competitor Analysis</h3>
          <p className={`text-mono mb-6 max-w-md mx-auto text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Create search queries that will be tested across AI search engines to discover who your competitors are and how visible they are compared to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={downloadSampleCSV}
              className="btn-glass px-4 py-2 rounded-lg text-mono font-medium text-sm dashboard-optimized"
              title="See example format for bulk importing queries"
            >
              Download Sample CSV
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-glass-accent px-4 py-2 rounded-lg text-mono font-medium text-sm dashboard-optimized"
              title="Upload CSV file with your search queries"
            >
              Import Your Templates
            </button>
          </div>
        </div>
      ) : (
        // Templates List
        <div>
          {/* Filters and Selection */}
          <div className="glass-card rounded-2xl p-4 mb-4 dashboard-optimized">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="glass-input px-3 py-2 rounded-lg text-white text-mono text-xs bg-black/20"
                  title="Filter templates by category"
                >
                  <option value="all">All Categories ({templates.length})</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.templateCount})
                    </option>
                  ))}
                </select>
                
                <button
                  onClick={handleSelectAll}
                  className="glass-interactive text-accent-400 hover:text-accent-300 text-mono text-xs font-medium px-3 py-2 rounded-lg"
                >
                  {selectedTemplates.size === filteredTemplates.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="glass-badge text-mono text-xs">
                  {selectedTemplates.size} of {filteredTemplates.length} selected
                </div>
                
                {selectedTemplates.size > 0 && (
                  <>
                    {selectedTemplates.size === 1 && (
                      <button
                        onClick={() => {
                          const templateId = Array.from(selectedTemplates)[0];
                          const template = filteredTemplates.find(t => t.id === templateId);
                          if (template) {
                            setRenameTemplate(template);
                            setShowRenameModal(true);
                          }
                        }}
                        disabled={isExecuting() || selectedTemplates.size === 0}
                        className="btn-glass px-4 py-2 rounded-lg text-mono font-semibold text-xs flex items-center gap-2 dashboard-optimized disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Rename
                      </button>
                    )}
                    <button
                      onClick={deleteSelectedTemplates}
                      disabled={isExecuting() || selectedTemplates.size === 0}
                      className="glass-badge-error px-4 py-2 rounded-lg text-mono font-semibold text-xs flex items-center gap-2 dashboard-optimized disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Permanently delete selected templates"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Selected
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Templates List */}
          <div className="glass-card rounded-xl dashboard-optimized p-4">
            <div className="max-h-96 overflow-y-auto custom-scrollbar divide-y divide-white/10">
              {filteredTemplates.map((template, index) => {
                const isSelected = selectedTemplates.has(template.id);
                const templateClasses = isSelected
                  ? 'flex items-center p-4 cursor-pointer transition-colors duration-200 bg-accent-400/10 border-l-4 border-accent-400'
                  : 'flex items-center p-4 cursor-pointer transition-colors duration-200 hover:bg-white/5 border-l-4 border-transparent';
                
                return (
                  <div key={template.id}>
                    <div
                      className={templateClasses}
                      onClick={() => handleTemplateSelection(template.id)}
                    >
                  {/* Checkbox */}
                  <div className="flex-shrink-0 mr-4">
                    <div className={`relative w-5 h-5 rounded border-2 transition-all duration-200 ${
                      isSelected
                        ? 'bg-accent-400/20 border-accent-400 shadow-accent-400/30'
                        : `border-gray-500 hover:border-gray-400 ${isDark ? 'bg-black/20' : 'bg-white/20'}`
                    } backdrop-blur-sm`}>
                      {isSelected && (
                        <svg 
                          className="absolute inset-0 w-full h-full text-accent-400 p-0.5" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={3} 
                            d="M5 13l4 4L19 7" 
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0 flex items-center">
                    <div className="flex-1">
                      <h3 className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {template.name}
                      </h3>
                      <p className={`text-mono text-sm mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {template.searchTerm}
                      </p>
                    </div>
                    
                    {template.categoryName && (
                      <span 
                        className="glass-badge text-mono text-xs font-medium px-2 py-1 rounded-full ml-3 flex-shrink-0"
                        style={{ 
                          backgroundColor: `${template.categoryColor}20`,
                          color: template.categoryColor,
                          borderColor: `${template.categoryColor}30`
                        }}
                      >
                        {template.categoryName}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 ml-4 flex items-center gap-2">
                    {/* View/Expand Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row selection
                        setExpandedTemplateId(expandedTemplateId === template.id ? null : template.id);
                      }}
                      className={`glass-interactive ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} hover:bg-blue-500/10 p-2 rounded-lg transition-all duration-200`}
                      title={expandedTemplateId === template.id ? "Hide details" : "View details"}
                    >
                      <svg className="w-4 h-4 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                           style={{ transform: expandedTemplateId === template.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    </div>
                    </div>
                    
                    {/* Expandable Details Section */}
                    {expandedTemplateId === template.id && (
                      <TemplateDetailsView 
                        template={template}
                        isDark={isDark}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <CSVImportModal 
          onClose={() => setShowImportModal(false)}
          onImportComplete={loadData}
        />
      )}
      
      
      
      {/* Rename Modal */}
      {showRenameModal && renameTemplate && (
        <RenameTemplateModal
          template={renameTemplate}
          onClose={() => {
            setShowRenameModal(false);
            setRenameTemplate(null);
          }}
          onRenameComplete={() => {
            loadData();
            setShowRenameModal(false);
            setRenameTemplate(null);
            setSelectedTemplates(new Set());
          }}
        />
      )}
    </div>
  );
});

// Simple CSV Import Modal Component
const CSVImportModal = ({ onClose, onImportComplete }) => {
  const { isDark } = useTheme();
  const [file, setFile] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
    
    // Auto-generate template name from filename if not set
    if (!templateName && e.target.files[0]) {
      const fileName = e.target.files[0].name.replace('.csv', '');
      setTemplateName(fileName);
    }
  };

  const handleImport = async () => {
    if (!file || !templateName.trim()) return;

    try {
      setImporting(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const formData = new FormData();
      formData.append('csvFile', file);
      formData.append('templateName', templateName.trim());

      const response = await fetch(`${apiUrl}/query-templates/import-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.result);
        setTimeout(() => {
          // Clear cache before reloading
          cacheManager.remove('templates');
          cacheManager.remove('categories');
          onImportComplete();
          onClose();
        }, 2000);
      } else {
        throw new Error('Import failed');
      }
    } catch (error) {
      console.error('Import failed:', error);
      setResult({ error: 'Import failed. Please check your CSV format.' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 dashboard-optimized">
      <div className="glass-modal rounded-2xl max-w-md w-full p-8 dashboard-optimized">
        <h3 className={`heading-mono text-base text-glow mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Import Query Templates</h3>
        
        <div className="space-y-6">
          <div>
            <label className={`block text-mono text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Template Name
            </label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name (e.g., 'Crisis PR Analysis')"
              className="w-full glass-input px-3 py-2 rounded-lg text-mono text-sm placeholder-gray-400 focus:outline-none"
            />
            <p className="text-mono text-xs text-gray-400 mt-2">
              This will be the name of your template that contains all the queries from your CSV.
            </p>
          </div>
          
          <div>
            <label className={`block text-mono text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-mono text-xs text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-mono file:text-xs file:font-medium file:glass-dark file:text-white hover:file:bg-white/10 file:transition-colors"
            />
            <p className="text-mono text-xs text-gray-400 mt-2">
              CSV should contain search queries, one per row. Each row will become a query within the template.
            </p>
          </div>
          
          {result && (
            <div className={`glass-alert rounded-lg p-4 ${result.error ? 'glass-alert-error' : 'glass-alert-success'}`}>
              {result.error ? (
                <p className="text-mono text-sm text-red-300">{result.error}</p>
              ) : (
                <div>
                  <p className="text-mono text-sm text-green-300 mb-2">✅ Import successful!</p>
                  <p className="text-mono text-xs text-green-200">
                    Imported: {result.imported}/{result.total} templates
                    {result.categories > 0 && `, Created: ${result.categories} categories`}
                  </p>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-3 glass-alert-warning rounded-lg p-3">
                      <p className="text-mono text-xs font-medium text-yellow-300 mb-1">Errors:</p>
                      <ul className="list-disc list-inside text-mono text-xs text-yellow-200">
                        {result.errors.map((error, index) => (
                          <li key={index}>Row {error.row}: {error.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="btn-glass px-4 py-2 rounded-lg text-mono font-medium text-sm dashboard-optimized"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file || !templateName.trim() || importing}
            className="btn-glass-accent px-4 py-2 rounded-lg text-mono font-medium text-sm disabled:opacity-50 dashboard-optimized disabled:hover:scale-100"
          >
            {importing ? (
              <div className="flex items-center gap-2">
                <div className="glass-spinner w-4 h-4"></div>
                Importing...
              </div>
            ) : (
              'Import'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Template Details View Component (inline expandable)
const TemplateDetailsView = ({ template, isDark }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTemplateDetails();
  }, [template.id]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchTemplateDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await queryTemplatesAPI.getTemplateById(template.id);
      setDetails(data.template);
    } catch (err) {
      console.error('Failed to fetch template details:', err);
      setError('Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`border-l-4 border-accent-400/50 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'} animate-expand-down`}>
      <div className="p-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="glass-spinner w-6 h-6"></div>
          </div>
        )}

        {error && (
          <div className="glass-alert-error rounded-lg p-4">
            <p className={`text-mono text-sm ${isDark ? 'text-red-300' : 'text-red-800'}`}>
              {error}
            </p>
          </div>
        )}

        {!loading && !error && details && (
          <div className="space-y-4">
            {/* Query Content */}
            <div>
              <h4 className={`text-mono text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Query Content
              </h4>
              {details.queries && Array.isArray(details.queries) ? (
                <div className="grid gap-2">
                  {details.queries.map((query, index) => (
                    <div key={index} className={`glass-card rounded-lg p-3 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'}`}>
                      <div className="flex items-start gap-3">
                        <span className={`text-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-0.5`}>
                          {index + 1}.
                        </span>
                        <div className="flex-1">
                          <p className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            {query.search_term}
                          </p>
                          {(query.location || query.device || query.search_engine) && (
                            <div className="flex gap-3 mt-2">
                              {query.location && (
                                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  📍 {query.location}
                                </span>
                              )}
                              {query.device && (
                                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  💻 {query.device}
                                </span>
                              )}
                              {query.search_engine && (
                                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  🔍 {query.search_engine}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : details.searchTerm ? (
                <div className={`glass-card rounded-lg p-3 ${isDark ? 'bg-gray-800/50' : 'bg-white/50'}`}>
                  <p className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {details.searchTerm}
                  </p>
                </div>
              ) : (
                <p className={`text-mono text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  No query content found
                </p>
              )}
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Priority</span>
                <p className={`text-mono text-sm font-medium mt-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {details.priority || 0}
                </p>
              </div>
              <div>
                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tags</span>
                <p className={`text-mono text-sm font-medium mt-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {details.tags?.length > 0 ? details.tags.join(', ') : 'None'}
                </p>
              </div>
              <div>
                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Created</span>
                <p className={`text-mono text-sm font-medium mt-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {new Date(details.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Template ID</span>
                <p className={`text-mono text-xs font-mono mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate`} title={details.id}>
                  {details.id.slice(0, 8)}...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Rename Template Modal Component
const RenameTemplateModal = ({ template, onClose, onRenameComplete }) => {
  const { isDark } = useTheme();
  const [newName, setNewName] = useState(template.name);
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState(null);

  const handleRename = async () => {
    if (!newName.trim()) {
      setError('Template name cannot be empty');
      return;
    }

    if (newName.trim() === template.name) {
      onClose();
      return;
    }

    try {
      setRenaming(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      const response = await fetchWithRetry(`${apiUrl}/query-templates/${template.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName.trim() })
      });

      if (response.ok) {
        onRenameComplete();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to rename template');
      }
    } catch (error) {
      console.error('Failed to rename template:', error);
      setError('Failed to rename template. Please try again.');
    } finally {
      setRenaming(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div 
          className="glass-modal rounded-2xl max-w-md w-full animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <h2 className={`heading-mono text-xl mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Rename Template
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-mono text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Template Name
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !renaming) {
                      handleRename();
                    }
                  }}
                  className="w-full glass-input px-3 py-2 rounded-lg text-mono text-sm placeholder-gray-400 focus:outline-none"
                  placeholder="Enter template name"
                  disabled={renaming}
                  autoFocus
                />
              </div>
              
              {error && (
                <div className="glass-alert-error rounded-lg p-3">
                  <p className={`text-mono text-sm ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                    {error}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleRename}
                disabled={renaming || !newName.trim()}
                className="flex-1 btn-glass-accent px-4 py-2 rounded-lg text-mono font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {renaming ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="glass-spinner w-4 h-4"></div>
                    Renaming...
                  </div>
                ) : (
                  'Rename'
                )}
              </button>
              <button
                onClick={onClose}
                disabled={renaming}
                className="btn-glass px-4 py-2 rounded-lg text-mono font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QueryTemplateManager;